import React, { useState, useEffect } from 'react';
import { 
    ThunderboltOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    ReloadOutlined,
    PlusOutlined,
    CloseCircleOutlined,
    SearchOutlined,
    EditOutlined
} from '@ant-design/icons';
import { 
    Table, 
    Tag, 
    Button, 
    Modal, 
    Select, 
    Input, 
    Form, 
    message, 
    Space, 
    Card, 
    Statistic, 
    Row, 
    Col, 
    Alert,
    Tooltip,
    Tabs,
    Badge,
    Switch,
    Typography
} from 'antd';
import styles from './styles/platform-health.module.scss';

const { Text } = Typography;

const OUTAGE_TYPES = {
    degraded: { label: 'Degraded', color: 'warning', severity: 3, icon: <WarningOutlined /> },
    partial: { label: 'Partial', color: 'orange', severity: 2, icon: <ExclamationCircleOutlined /> },
    full: { label: 'Full', color: 'error', severity: 1, icon: <CloseCircleOutlined /> }
};

const PROVIDERS = ['pinwheel', 'atomic', 'argyle', 'truv'];

function PlatformHealth({ data, loading }) {
    const [platforms, setPlatforms] = useState([]);
    const [outages, setOutages] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [selectedOutage, setSelectedOutage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [providerFilter, setProviderFilter] = useState(null);
    const [outagesPagination, setOutagesPagination] = useState({ current: 1, pageSize: 10 });
    const [newlyCreatedOutageId, setNewlyCreatedOutageId] = useState(null);
    const [duplicateOutageConfirm, setDuplicateOutageConfirm] = useState(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [outageTypeHelp, setOutageTypeHelp] = useState(null);

    useEffect(() => {
        if (data?.fetchPlatforms && data?.fetchPlatformOutages) {
            loadData();
        }
    }, [data]);

    // Auto-reload when filters change
    useEffect(() => {
        if (data?.fetchPlatforms && data?.fetchPlatformOutages) {
            const timeoutId = setTimeout(() => {
                loadData();
            }, searchTerm ? 300 : 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [providerFilter, searchTerm]);

    // Navigate to newly created/updated outage
    useEffect(() => {
        if (newlyCreatedOutageId && outages.length > 0) {
            const outageIndex = outages.findIndex(o => o.id === newlyCreatedOutageId);
            if (outageIndex >= 0) {
                const pageSize = outagesPagination.pageSize;
                const targetPage = Math.floor(outageIndex / pageSize) + 1;
                
                setOutagesPagination(prev => ({ ...prev, current: targetPage }));
                
                setTimeout(() => {
                    const outageElement = document.querySelector(`[data-outage-id="${newlyCreatedOutageId}"]`);
                    if (outageElement) {
                        outageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        outageElement.style.transition = 'background-color 0.3s';
                        outageElement.style.backgroundColor = '#e6f7ff';
                        setTimeout(() => {
                            outageElement.style.backgroundColor = '';
                            setNewlyCreatedOutageId(null);
                        }, 2000);
                    }
                }, 300);
            }
        }
    }, [newlyCreatedOutageId, outages, outagesPagination.pageSize]);

    const loadData = async () => {
        try {
            const [platformsResult, outagesResult] = await Promise.all([
                data.fetchPlatforms(providerFilter, searchTerm || null),
                data.fetchPlatformOutages(null, providerFilter, 'active')
            ]);
            
            const loadedPlatforms = platformsResult?.platforms || [];
            const loadedOutages = outagesResult?.outages || [];
            
            setPlatforms(loadedPlatforms);
            
            // Filter outages to only show those whose platforms match the search
            const filteredOutages = searchTerm 
                ? loadedOutages.filter(outage => {
                    const platform = loadedPlatforms.find(p => p.id === outage.platform_id);
                    return platform && platform.name.toLowerCase().includes(searchTerm.toLowerCase());
                })
                : loadedOutages;
            
            setOutages(filteredOutages);
        } catch (error) {
            console.error('Error loading platform health data:', error);
            message.error('Failed to load platform health data');
        }
    };

    const handleRefresh = async () => {
        await loadData();
    };

    const handleCreateOutage = async (values, skipDuplicateCheck = false) => {
        try {
            const platformId = values.platform_id;
            const selectedPlatform = platforms.find(p => p.id === platformId);
            const provider = selectedPlatform?.provider ?? values.provider;

            if (!platformId) {
                message.error('Please select a platform');
                return;
            }
            if (!provider) {
                message.error('Provider could not be determined. Please select a platform again.');
                return;
            }

            // Check for existing active outage for this platform
            if (!skipDuplicateCheck) {
                const existingOutage = outages.find(
                    o => o.platform_id === platformId && o.status === 'active'
                );
                
                if (existingOutage) {
                    setDuplicateOutageConfirm({
                        platformId,
                        platformName: selectedPlatform?.name || 'Unknown Platform',
                        existingOutage,
                        formValues: values
                    });
                    return;
                }
            }

            const payload = {
                platform_id: platformId,
                provider,
                outage_type: values.outage_type,
                message: values.message,
                has_fallback: values.has_fallback ?? true
            };
            
            const response = await data.createPlatformOutage(payload);
            const outage = response?.outage || response?.data?.outage;
            const outageTypeLabel = OUTAGE_TYPES[values.outage_type]?.label || values.outage_type;
            const platformName = selectedPlatform?.name || 'Unknown Platform';
            const outageId = outage?.id;
            
            setCreateModalOpen(false);
            setDuplicateOutageConfirm(null);
            form.resetFields();
            
            if (outageId) {
                setNewlyCreatedOutageId(outageId);
            }
            
            await loadData();
            
            message.success({
                content: (
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: '14px' }}>
                            ✓ Platform outage created successfully!
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.8' }}>
                            <div style={{ marginBottom: 4 }}><strong>Platform:</strong> {platformName}</div>
                            <div style={{ marginBottom: 4 }}><strong>Type:</strong> {outageTypeLabel}</div>
                            {outageId && <div><strong>Outage ID:</strong> {outageId.substring(0, 8)}...</div>}
                            <div style={{ marginTop: 6, fontStyle: 'italic', color: '#1890ff' }}>
                                The outage has been added to the table below.
                            </div>
                        </div>
                    </div>
                ),
                duration: 6,
            });
        } catch (error) {
            const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create platform outage';
            message.error({
                content: `Failed to create platform outage: ${errorMessage}`,
                duration: 5,
            });
        }
    };

    const handleUpdateOutage = async (values) => {
        if (!selectedOutage) return;

        try {
            const payload = {
                outage_type: values.outage_type,
                message: values.message,
                has_fallback: values.has_fallback !== undefined ? values.has_fallback : (selectedOutage?.has_fallback ?? true)
            };
            
            await data.updatePlatformOutage(selectedOutage.id, payload);
            const outageTypeLabel = OUTAGE_TYPES[values.outage_type]?.label || values.outage_type;
            
            setEditModalOpen(false);
            setSelectedOutage(null);
            editForm.resetFields();
            
            setNewlyCreatedOutageId(selectedOutage.id);
            await loadData();
            
            message.success({
                content: `Platform outage updated successfully. Type: ${outageTypeLabel}`,
                duration: 4,
            });
        } catch (error) {
            const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update platform outage';
            message.error({
                content: `Failed to update platform outage: ${errorMessage}`,
                duration: 5,
            });
        }
    };

    const handleResolveOutage = async () => {
        if (!selectedOutage) return;

        try {
            await data.resolvePlatformOutage(selectedOutage.id);
            message.success('Platform outage resolved successfully');
            setResolveModalOpen(false);
            setSelectedOutage(null);
            await loadData();
        } catch (error) {
            message.error('Failed to resolve platform outage');
        }
    };

    const handleOpenEditModal = (outage) => {
        setSelectedOutage(outage);
        editForm.setFieldsValue({
            outage_type: outage.outage_type,
            message: outage.message,
            has_fallback: outage.has_fallback ?? true
        });
        
        // Initialize contextual help based on current outage type
        const helpMessages = {
            degraded: {
                type: 'info',
                title: 'Degraded Performance',
                message: 'Platform is slow but operational. Check if standard web method is affected but native app mode can still link payroll. If native app mode works, enable alternative method.'
            },
            partial: {
                type: 'warning',
                title: 'Partial Outage',
                message: 'Some features unavailable. Determine if the outage affects standard web method only (enable alternative method) or both methods (disable alternative method).'
            },
            full: {
                type: 'error',
                title: 'Full Outage',
                message: 'Platform completely unavailable. Usually both standard web method and native app mode are affected. Disable alternative method unless you\'ve confirmed native app mode can still link payroll.'
            }
        };
        setOutageTypeHelp(helpMessages[outage.outage_type] || null);
        
        setEditModalOpen(true);
    };

    const handleOpenResolveModal = (outage) => {
        setSelectedOutage(outage);
        setResolveModalOpen(true);
    };

    // Statistics
    const activeOutagesCount = outages.length;
    const degradedCount = outages.filter(o => o.outage_type === 'degraded').length;
    const partialCount = outages.filter(o => o.outage_type === 'partial').length;
    const fullCount = outages.filter(o => o.outage_type === 'full').length;

    const outageColumns = [
        {
            title: 'Platform',
            key: 'platform',
            render: (_, record) => {
                const platform = platforms.find(p => p.id === record.platform_id);
                return (
                    <div>
                        <div className={styles.platformName}>
                            {platform?.name || 'Unknown Platform'}
                        </div>
                        <Tag size="small">{record.provider}</Tag>
                    </div>
                );
            },
            width: 200
        },
        {
            title: 'Type',
            dataIndex: 'outage_type',
            key: 'outage_type',
            render: (type) => {
                const config = OUTAGE_TYPES[type] || { label: type, color: 'default', icon: <WarningOutlined /> };
                return (
                    <Tag color={config.color} icon={config.icon}>
                        {config.label}
                    </Tag>
                );
            },
            width: 120
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            ellipsis: true,
            render: (msg) => msg || 'No message provided',
            width: 200
        },
        {
            title: 'Alternative Method',
            dataIndex: 'has_fallback',
            key: 'has_fallback',
            render: (hasFallback, record) => {
                const platform = platforms.find(p => p.id === record.platform_id);
                const provider = record.provider || platform?.provider || 'unknown';
                
                return (
                    <Tooltip 
                        title={
                            <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                {hasFallback ? (
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: 4 }}>✓ Alternative Method Available</div>
                                        <div>Standard web link method is affected, but users can still link payroll using native app mode.</div>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: 4 }}>✗ Alternative Method Unavailable</div>
                                        <div>Both standard web method and native app mode are affected.</div>
                                        <div style={{ marginTop: 4 }}>Payroll linking will be unavailable.</div>
                                    </div>
                                )}
                            </div>
                        }
                    >
                        <Tag 
                            color={hasFallback ? 'success' : 'default'} 
                            icon={hasFallback ? <CheckCircleOutlined /> : null}
                            style={{ cursor: 'help' }}
                        >
                            {hasFallback ? 'Available' : 'Unavailable'}
                        </Tag>
                    </Tooltip>
                );
            },
            width: 160
        },
        {
            title: 'Started',
            dataIndex: 'started_at',
            key: 'started_at',
            render: (date) => date ? new Date(date).toLocaleString() : '-',
            width: 180
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, record) => {
                if (!record.started_at) return '-';
                const duration = Math.floor((new Date() - new Date(record.started_at)) / 1000 / 60);
                if (duration < 60) return `${duration}m`;
                const hours = Math.floor(duration / 60);
                const minutes = duration % 60;
                return `${hours}h ${minutes}m`;
            },
            width: 120
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button 
                        size="small" 
                        icon={<EditOutlined />}
                        onClick={() => handleOpenEditModal(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleOpenResolveModal(record)}
                    >
                        Resolve
                    </Button>
                </Space>
            ),
            width: 180
        }
    ];

    const platformColumns = [
        {
            title: 'Platform Name',
            dataIndex: 'name',
            key: 'name',
            width: 250
        },
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
            render: (provider) => <Tag>{provider}</Tag>,
            width: 120
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const platformOutages = outages.filter(o => o.platform_id === record.id);
                if (platformOutages.length === 0) {
                    return <Tag color="success" icon={<CheckCircleOutlined />}>Healthy</Tag>;
                }
                const mostSevere = platformOutages.sort((a, b) => 
                    OUTAGE_TYPES[a.outage_type].severity - OUTAGE_TYPES[b.outage_type].severity
                )[0];
                const config = OUTAGE_TYPES[mostSevere.outage_type];
                const hasFallback = mostSevere.has_fallback;
                return (
                    <Space direction="vertical" size={4}>
                        <Tag color={config.color} icon={config.icon}>
                            {config.label} Outage
                        </Tag>
                        {hasFallback && (
                            <Tag color="success" size="small" icon={<CheckCircleOutlined />}>
                                Native App Available
                            </Tag>
                        )}
                    </Space>
                );
            },
            width: 180
        },
        {
            title: 'Active Outages',
            key: 'outage_count',
            render: (_, record) => {
                const platformOutages = outages.filter(o => o.platform_id === record.id);
                return platformOutages.length > 0 ? (
                    <Badge count={platformOutages.length} size="small" />
                ) : (
                    <span>-</span>
                );
            },
            width: 120
        }
    ];

    return (
        <div className={styles.platformHealth}>
            <div className={styles.header}>
                <h2>Platform Health Management</h2>
                <Space>
                    <Select
                        style={{ width: 150 }}
                        placeholder="Filter by provider"
                        allowClear
                        value={providerFilter}
                        onChange={(value) => {
                            setProviderFilter(value);
                        }}
                        options={PROVIDERS.map(p => ({ label: p.charAt(0).toUpperCase() + p.slice(1), value: p }))}
                    />
                    <Input
                        placeholder="Search platforms..."
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                        }}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Button 
                        type="primary"
                        icon={<PlusOutlined />} 
                        onClick={() => setCreateModalOpen(true)}
                    >
                        Create Outage
                    </Button>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={handleRefresh}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </Space>
            </div>

            <Row gutter={16} className={styles.statsRow}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Active Outages"
                            value={activeOutagesCount}
                            prefix={<ThunderboltOutlined />}
                            valueStyle={{ color: activeOutagesCount > 0 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Degraded"
                            value={degradedCount}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: degradedCount > 0 ? '#faad14' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Partial"
                            value={partialCount}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: partialCount > 0 ? '#ff9800' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="Full Outage"
                            value={fullCount}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: fullCount > 0 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Tabs
                defaultActiveKey="outages"
                items={[
                    {
                        key: 'outages',
                        label: `Active Outages (${activeOutagesCount})`,
                        children: (
                            <Table
                                columns={outageColumns}
                                dataSource={outages}
                                rowKey="id"
                                loading={loading}
                                pagination={{
                                    current: outagesPagination.current,
                                    pageSize: outagesPagination.pageSize,
                                    onChange: (page, pageSize) => {
                                        setOutagesPagination({ current: page, pageSize });
                                    },
                                    showSizeChanger: true,
                                    showTotal: (total) => `Total ${total} outages`,
                                }}
                                scroll={{ x: 1060 }}
                                locale={{ emptyText: 'No active outages' }}
                                onRow={(record) => ({
                                    'data-outage-id': record.id,
                                })}
                            />
                        )
                    },
                    {
                        key: 'platforms',
                        label: `Platforms (${platforms.length})`,
                        children: (
                            <Table
                                columns={platformColumns}
                                dataSource={platforms}
                                rowKey="id"
                                loading={loading}
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 700 }}
                            />
                        )
                    }
                ]}
            />

            <Modal
                title="Create Platform Outage"
                open={createModalOpen}
                onCancel={() => {
                    setCreateModalOpen(false);
                    form.resetFields();
                    setOutageTypeHelp(null);
                }}
                afterClose={() => {
                    setOutageTypeHelp(null);
                }}
                onOk={() => form.submit()}
                okText="Create"
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateOutage}
                >
                    <Form.Item
                        name="platform_id"
                        label="Platform"
                        rules={[{ required: true, message: 'Please select a platform' }]}
                    >
                        <Select
                            placeholder="Select platform"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={platforms.map(p => ({
                                value: p.id,
                                label: `${p.name} (${p.provider})`
                            }))}
                            onChange={(value) => {
                                const selectedPlatform = platforms.find(p => p.id === value);
                                if (selectedPlatform) {
                                    form.setFieldsValue({ provider: selectedPlatform.provider });
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="provider"
                        label="Provider"
                        tooltip="Automatically set from the selected platform"
                    >
                        <Select
                            placeholder="Provider (auto-filled from platform)"
                            disabled={true}
                            options={PROVIDERS.map(p => ({ value: p, label: p.charAt(0).toUpperCase() + p.slice(1) }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="outage_type"
                        label="Outage Type"
                        rules={[{ required: true, message: 'Please select outage type' }]}
                    >
                        <Select
                            placeholder="Select outage type"
                            options={Object.entries(OUTAGE_TYPES).map(([key, config]) => ({
                                value: key,
                                label: (
                                    <span>
                                        <Tag color={config.color} style={{ marginRight: 8 }}>
                                            {config.label}
                                        </Tag>
                                    </span>
                                )
                            }))}
                            onChange={(value) => {
                                // Show contextual help based on outage type
                                const helpMessages = {
                                    degraded: {
                                        type: 'info',
                                        title: 'Degraded Performance',
                                        message: 'Platform is slow but operational. Check if standard web method is affected but native app mode still works. If native app mode works, enable alternative method.'
                                    },
                                    partial: {
                                        type: 'warning',
                                        title: 'Partial Outage',
                                        message: 'Some features unavailable. Determine if the outage affects standard web method only (enable alternative method) or both methods (disable alternative method).'
                                    },
                                    full: {
                                        type: 'error',
                                        title: 'Full Outage',
                                        message: 'Platform completely unavailable. Usually both standard web method and native app mode are affected. Disable alternative method unless you\'ve confirmed native app mode can still link payroll.'
                                    }
                                };
                                setOutageTypeHelp(helpMessages[value] || null);
                            }}
                        />
                    </Form.Item>
                    {outageTypeHelp && (
                        <Alert
                            message={outageTypeHelp.title}
                            description={outageTypeHelp.message}
                            type={outageTypeHelp.type}
                            showIcon
                            style={{ marginBottom: 16 }}
                            closable
                            onClose={() => setOutageTypeHelp(null)}
                        />
                    )}
                    <Form.Item
                        name="has_fallback"
                        label="Alternative Link Method Available"
                        tooltip="Enable if standard web method is affected but native app mode still works. Disable if both methods are affected."
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch 
                            checkedChildren="Available" 
                            unCheckedChildren="Unavailable"
                        />
                    </Form.Item>
                    <Form.Item
                        name="message"
                        label="Message (Broadcasted to Users)"
                        rules={[{ required: true, message: 'Please enter a message' }]}
                        tooltip="This message will be broadcasted to all connected users. Write a clear, user-friendly explanation of the outage."
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Example: 'Paycom is experiencing technical difficulties. You may experience delays when linking your payroll account.'"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Edit Platform Outage"
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    setSelectedOutage(null);
                    editForm.resetFields();
                    setOutageTypeHelp(null);
                }}
                afterClose={() => {
                    setOutageTypeHelp(null);
                    setSelectedOutage(null);
                }}
                onOk={() => editForm.submit()}
                okText="Update"
                width={600}
            >
                {selectedOutage && (() => {
                    const platform = platforms.find(p => p.id === selectedOutage.platform_id);
                    return (
                        <Form
                            form={editForm}
                            layout="vertical"
                            onFinish={handleUpdateOutage}
                        >
                            <div style={{ 
                                marginBottom: 20, 
                                padding: 12, 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: 4,
                                border: '1px solid #d9d9d9'
                            }}>
                                <Space direction="vertical" size={4}>
                                    <div>
                                        <Text strong>Platform: </Text>
                                        <Text>{platform?.name || 'Unknown Platform'}</Text>
                                    </div>
                                    <div>
                                        <Text strong>Provider: </Text>
                                        <Tag>{platform?.provider || 'Unknown'}</Tag>
                                    </div>
                                </Space>
                            </div>
                            <Form.Item
                                name="outage_type"
                                label="Outage Type"
                                rules={[{ required: true, message: 'Please select outage type' }]}
                            >
                            <Select
                                placeholder="Select outage type"
                                options={Object.entries(OUTAGE_TYPES).map(([key, config]) => ({
                                    value: key,
                                    label: (
                                        <span>
                                            <Tag color={config.color} style={{ marginRight: 8 }}>
                                                {config.label}
                                            </Tag>
                                        </span>
                                    )
                                }))}
                                onChange={(value) => {
                                    // Show contextual help based on outage type
                                    const helpMessages = {
                                        degraded: {
                                            type: 'info',
                                            title: 'Degraded Performance',
                                            message: 'Platform is slow but operational. Check if standard web method is affected but native app mode can still link payroll. If native app mode works, enable alternative method.'
                                        },
                                        partial: {
                                            type: 'warning',
                                            title: 'Partial Outage',
                                            message: 'Some features unavailable. Determine if the outage affects standard web method only (enable alternative method) or both methods (disable alternative method).'
                                        },
                                        full: {
                                            type: 'error',
                                            title: 'Full Outage',
                                            message: 'Platform completely unavailable. Usually both standard web method and native app mode are affected. Disable alternative method unless you\'ve confirmed native app mode can still link payroll.'
                                        }
                                    };
                                    setOutageTypeHelp(helpMessages[value] || null);
                                }}
                            />
                            </Form.Item>
                            <Form.Item
                                name="has_fallback"
                                label={
                                    <span>
                                        Alternative Link Method Available
                                        {outageTypeHelp && (
                                            <Tooltip title={outageTypeHelp.message}>
                                                <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px', fontWeight: 'normal' }}>
                                                    ({outageTypeHelp.title})
                                                </Text>
                                            </Tooltip>
                                        )}
                                    </span>
                                }
                                tooltip="Enable if standard web method is affected but native app mode can still link payroll. Disable if both methods are affected."
                                valuePropName="checked"
                            >
                                <Switch 
                                    checkedChildren="Available" 
                                    unCheckedChildren="Unavailable"
                                />
                            </Form.Item>
                            <Form.Item
                                name="message"
                                label="Message (Broadcasted to Users)"
                                rules={[{ required: true, message: 'Please enter a message' }]}
                                tooltip="This message will be broadcasted to all connected users. Write a clear, user-friendly explanation of the outage."
                            >
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Example: 'Paycom is experiencing technical difficulties. You may experience delays when linking your payroll account.'"
                                />
                            </Form.Item>
                        </Form>
                    );
                })()}
            </Modal>

            <Modal
                title="Resolve Platform Outage"
                open={resolveModalOpen}
                onCancel={() => {
                    setResolveModalOpen(false);
                    setSelectedOutage(null);
                }}
                onOk={handleResolveOutage}
                okText="Resolve"
            >
                {selectedOutage && (
                    <div>
                        <Alert
                            message="Resolve Platform Outage"
                            description={
                                <div>
                                    <p><strong>Platform:</strong> {platforms.find(p => p.id === selectedOutage.platform_id)?.name || 'Unknown'}</p>
                                    <p><strong>Type:</strong> <Tag color={OUTAGE_TYPES[selectedOutage.outage_type]?.color}>
                                        {OUTAGE_TYPES[selectedOutage.outage_type]?.label}
                                    </Tag></p>
                                    <p><strong>Message:</strong> {selectedOutage.message || 'No message'}</p>
                                    <p>
                                        <strong>Alternative Link Method:</strong>{' '}
                                        <Tag color={selectedOutage.has_fallback ? 'success' : 'default'}>
                                            {selectedOutage.has_fallback ? 'Available' : 'Unavailable'}
                                        </Tag>
                                    </p>
                                    {selectedOutage.has_fallback ? (
                                        <p style={{ marginLeft: 16, fontSize: '12px', color: '#52c41a', fontStyle: 'italic' }}>
                                            ✓ Standard web method is affected, but users can still link payroll using native app mode.
                                        </p>
                                    ) : (
                                        <p style={{ marginLeft: 16, fontSize: '12px', color: '#8c8c8c', fontStyle: 'italic' }}>
                                            ✗ Both standard web method and native app mode are affected. Payroll linking will be unavailable.
                                        </p>
                                    )}
                                    <p><strong>Started:</strong> {new Date(selectedOutage.started_at).toLocaleString()}</p>
                                </div>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                        <p>This will resolve the outage and notify all affected users that service has been restored.</p>
                    </div>
                )}
            </Modal>

            <Modal
                title="Active Outage Already Exists"
                open={!!duplicateOutageConfirm}
                onCancel={() => {
                    setDuplicateOutageConfirm(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => setDuplicateOutageConfirm(null)}>
                        Cancel
                    </Button>,
                    <Button 
                        key="edit" 
                        type="primary"
                        onClick={async () => {
                            if (duplicateOutageConfirm) {
                                const existingOutage = duplicateOutageConfirm.existingOutage;
                                const formValues = duplicateOutageConfirm.formValues;
                                
                                // Close modals and reset form
                                setDuplicateOutageConfirm(null);
                                setCreateModalOpen(false);
                                form.resetFields();
                                
                                // Set the selected outage and update it directly with form values
                                setSelectedOutage(existingOutage);
                                
                                    // Update the outage with the form values
                                    try {
                                        const payload = {
                                            outage_type: formValues.outage_type,
                                            message: formValues.message,
                                            has_fallback: formValues.has_fallback ?? true
                                        };
                                    
                                    await data.updatePlatformOutage(existingOutage.id, payload);
                                    const outageTypeLabel = OUTAGE_TYPES[formValues.outage_type]?.label || formValues.outage_type;
                                    
                                    setSelectedOutage(null);
                                    
                                    setNewlyCreatedOutageId(existingOutage.id);
                                    await loadData();
                                    
                                    message.success({
                                        content: `Platform outage updated successfully. Type: ${outageTypeLabel}`,
                                        duration: 4,
                                    });
                                } catch (error) {
                                    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update platform outage';
                                    message.error({
                                        content: `Failed to update platform outage: ${errorMessage}`,
                                        duration: 5,
                                    });
                                }
                            }
                        }}
                    >
                        Update Existing Outage
                    </Button>,
                    <Button 
                        key="create" 
                        type="primary" 
                        danger
                        onClick={() => {
                            if (duplicateOutageConfirm) {
                                handleCreateOutage(duplicateOutageConfirm.formValues, true);
                            }
                        }}
                    >
                        Create Anyway
                    </Button>
                ]}
                width={600}
            >
                {duplicateOutageConfirm && (() => {
                    const existingOutage = duplicateOutageConfirm.existingOutage;
                    const outageAge = new Date() - new Date(existingOutage.started_at);
                    const ageMinutes = Math.floor(outageAge / 1000 / 60);
                    const ageHours = Math.floor(ageMinutes / 60);
                    const ageDays = Math.floor(ageHours / 24);
                    
                    let ageMessage = '';
                    if (ageMinutes < 5) {
                        ageMessage = `This outage was created ${ageMinutes} minute${ageMinutes !== 1 ? 's' : ''} ago. Updating it is recommended to avoid duplicate notifications.`;
                    } else if (ageMinutes < 60) {
                        ageMessage = `This outage was created ${ageMinutes} minute${ageMinutes !== 1 ? 's' : ''} ago.`;
                    } else if (ageHours < 24) {
                        ageMessage = `This outage was created ${ageHours} hour${ageHours !== 1 ? 's' : ''} ago.`;
                    } else {
                        ageMessage = `This outage was created ${ageDays} day${ageDays !== 1 ? 's' : ''} ago.`;
                    }
                    
                    return (
                        <div>
                            <Alert
                                message="An active outage already exists for this platform"
                                description={
                                    <div>
                                        <p style={{ marginBottom: 12 }}>
                                            There is already an active outage for <strong>{duplicateOutageConfirm.platformName}</strong>.
                                            Creating a new outage may cause confusion or duplicate notifications.
                                        </p>
                                        <div style={{ 
                                            backgroundColor: '#fff7e6', 
                                            padding: 12, 
                                            borderRadius: 4,
                                            border: '1px solid #ffd591',
                                            marginBottom: 12
                                        }}>
                                            <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>Existing Outage:</p>
                                            <p style={{ margin: '4px 0' }}>
                                                <strong>Type:</strong> <Tag color={OUTAGE_TYPES[existingOutage.outage_type]?.color}>
                                                    {OUTAGE_TYPES[existingOutage.outage_type]?.label}
                                                </Tag>
                                            </p>
                                            <p style={{ margin: '4px 0' }}>
                                                <strong>Message:</strong> {existingOutage.message || 'No message'}
                                            </p>
                                            <p style={{ margin: '4px 0' }}>
                                                <strong>Native App Mode:</strong>{' '}
                                                <Tag color={existingOutage.has_fallback ? 'success' : 'default'} size="small">
                                                    {existingOutage.has_fallback ? 'Available' : 'Unavailable'}
                                                </Tag>
                                            </p>
                                            <p style={{ margin: '4px 0' }}>
                                                <strong>Started:</strong> {new Date(existingOutage.started_at).toLocaleString()}
                                            </p>
                                            {ageMessage && (
                                                <p style={{ margin: '8px 0 0 0', fontStyle: 'italic', color: ageMinutes < 5 ? '#ff4d4f' : '#666' }}>
                                                    {ageMessage}
                                                </p>
                                            )}
                                        </div>
                                        <p style={{ marginTop: 12, color: '#666' }}>
                                            Would you like to update the existing outage, create a new one anyway, or cancel?
                                        </p>
                                    </div>
                                }
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
}

export default PlatformHealth;
